import { useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { ExpandMore, ExpandLess, LocationOnOutlined } from '@material-ui/icons'

import { AddressCard, TOAST_TYPE } from '@base'
import { AddressDetailsCard } from '@baseV2'
import { getUserAddressesApi } from '@data'
import { Address } from '@data/model'
import { showToast, showLoader } from '@data/state/action'
import { strings } from '@constants'
import { Spacings, Text } from '@styles'

import BasicModal from '@base/basic/BasicModal'

import { saveInvoice, buildBaseInvoicePayload } from './invoiceApi'
import { BasicDetailsCard, CardHeader, CardTitle, ChevronBtn } from './style'

const ModalTitle = styled.h5`
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.palette.text.darkblack};
	padding-bottom: ${Spacings.SPACING_3B};
	border-bottom: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	margin: 0 0 ${Spacings.SPACING_2B};
`

const AddressList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
`

const AddressOption = styled.button`
	text-align: left;
	cursor: pointer;
	background: ${({ theme }) => theme.palette.background.white};
	border: 1px solid ${({ $selected, theme }) => ($selected ? theme.colors.brand.primary : theme.palette.background.greyBorder)};
	border-radius: ${Spacings.SPACING_5};
	padding: ${Spacings.SPACING_1B};

	&:hover {
		border-color: ${({ theme }) => theme.colors.brand.primary};
	}
`

const EmptyText = styled.p`
	margin: 0;
	text-align: center;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const DispatchLocation = ({ invoiceData, order, supplierPoNumber, update, editable = false }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const [isOpen, setIsOpen] = useState(true)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [addresses, setAddresses] = useState([])

	// The supplier entry that owns this invoice: its `supplier` user holds the address book and its
	// `dispatch_location` is the default shown when the invoice has none of its own.
	const supplierEntry = useMemo(() => {
		const suppliers = order?.supplier_list ?? []
		if (supplierPoNumber) return suppliers.find((s) => s?.supplier_po_number === supplierPoNumber)
		return suppliers.find((s) => s?.supplier_invoices?.some((i) => i?.invoice_number === invoiceData?.invoice_number))
	}, [order, supplierPoNumber, invoiceData?.invoice_number])
	const supplier = supplierEntry?.supplier

	const dispatchLocation = invoiceData?.dispatch_location || supplierEntry?.dispatch_location

	const saveLocation = useCallback(
		(location) =>
			saveInvoice({
				payload: {
					...invoiceData,
					...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber),
					dispatch_location: location,
					updated_by: user?.user_id,
				},
				fallbackInvoice: { ...invoiceData, dispatch_location: location },
				order,
				invoiceData,
				dispatch,
				supplierPoNumber,
				update,
			}),
		[invoiceData, order, supplierPoNumber, user, dispatch, update]
	)

	const handleEdit = useCallback(() => {
		dispatch(showLoader(true))
		getUserAddressesApi(supplier?.user_id)
			.then((res) => {
				setAddresses(Array.isArray(res) ? res : [])
				setIsModalOpen(true)
			})
			.catch((err) => dispatch(showToast(true, err?.response?.data?.message, { type: TOAST_TYPE.ERROR })))
			.finally(() => dispatch(showLoader(false)))
	}, [supplier, dispatch])

	const handleSelect = useCallback(
		async (address) => {
			dispatch(showLoader(true))
			const ok = await saveLocation(new Address(address))
			dispatch(showLoader(false))
			if (ok) setIsModalOpen(false)
		},
		[saveLocation, dispatch]
	)

	return (
		<BasicDetailsCard>
			<CardHeader $noBorder={!isOpen}>
				<CardTitle>{strings('dispatch_location')}</CardTitle>
				<ChevronBtn onClick={() => setIsOpen((v) => !v)}>{isOpen ? <ExpandLess /> : <ExpandMore />}</ChevronBtn>
			</CardHeader>

			{isOpen && (
				<AddressDetailsCard
					title={strings('address')}
					icon={LocationOnOutlined}
					actionLabel={strings('edit')}
					onEdit={editable ? handleEdit : undefined}
					addressData={dispatchLocation}
					isAddressSelected={!!dispatchLocation}
					showSelectAddressInHeader
				/>
			)}

			<BasicModal width='40vw' isOpen={isModalOpen} hideScrollbar onClose={() => setIsModalOpen(false)} closeIcon={true}>
				<ModalTitle>{strings('select_address')}</ModalTitle>
				<AddressList>
					{addresses.length ? (
						addresses.map((address) => (
							<AddressOption
								key={address.address_id}
								type='button'
								$selected={address.address_id === dispatchLocation?.address_id}
								onClick={() => handleSelect(address)}
							>
								<AddressCard data={address} />
							</AddressOption>
						))
					) : (
						<EmptyText>{strings('no_address_selected')}</EmptyText>
					)}
				</AddressList>
			</BasicModal>
		</BasicDetailsCard>
	)
}

export default DispatchLocation
